import { useState, useRef, useEffect, useCallback } from "react";
import { Send, Home, User, RotateCcw, Expand, Shrink, Sparkles, Calendar, Layers, RefreshCw } from "lucide-react";
import { motion } from "framer-motion";
import ReactMarkdown from "react-markdown";
import { supabase } from "@/integrations/supabase/client";
import { createConsultFollowup } from "@/lib/consultFollowup.functions";
import { useAuth } from "@/hooks/useAuth";
import { Link, useSearchParams } from "@/lib/router-compat";
import HomieLifeMartCard from "./HomieLifeMartCard";
import { EmployerAccessGate } from "@/components/access/EmployerAccessGate";
import { useEmployerAccess } from "@/hooks/useEmployerAccess";
import { homieMessagesRemaining } from "@/lib/accessPreview";
import { getStage2ForTrack, getStage2HumanForTrack } from "@/lib/stage2Consult";
import { upsertUserAttributes } from "@/lib/userAttributes";
import { dgFeatureClick } from "@/lib/dataGraph";
import { logLpEvent, logLpEventOnce } from "@/lib/lpEvents";

type Message = { role: "user" | "assistant"; content: string; lifemartCard?: boolean };

export type HomiePreviewMode = "default" | "lifemart" | "lifemart-with-card";

const HOMIE_URL = "/api/homie-chat";

const QUICK_REPLIES_GUEST = [
  "How do I start buying a home?",
  "What credit score do I need?",
  "How much do I need for a down payment?",
  "What are closing costs?",
];

const QUICK_REPLIES_USER = [
  "What should I focus on next?",
  "Am I ready to buy?",
  "Help me make a savings plan",
  "Explain pre-approval",
];

const QUICK_REPLIES_LIFEMART = [
  "Is Home Approach really free for me?",
  "How do I redeem my LifeMart code?",
  "What if my employer isn't enrolled?",
  "What do I get with 25 free points?",
];

interface HomieChatProps {
  fullPage?: boolean;
  previewMode?: HomiePreviewMode;
  /** When provided, the chat auto-sends this question on mount. */
  seedPrompt?: string;
  onSeedConsumed?: () => void;
}

const HomieChat = ({ fullPage = false, previewMode = "default", seedPrompt, onSeedConsumed }: HomieChatProps) => {
  const { user } = useAuth();
  const { hasAccess, loading: accessLoading } = useEmployerAccess();
  const [freeUsed, setFreeUsed] = useState(0);
  const freeUsedRef = useRef(0);
  const accessRef = useRef({ hasAccess, accessLoading });
  accessRef.current = { hasAccess, accessLoading };
  useEffect(() => {
    try {
      const n = Math.max(0, Math.floor(Number(localStorage.getItem("ha_homie_free_msgs") ?? "0")) || 0);
      freeUsedRef.current = n;
      setFreeUsed(n);
    } catch { /* ignore */ }
  }, []);
  const freeLimitReached = !accessLoading && !hasAccess && homieMessagesRemaining(freeUsed) === 0;
  const [searchParams] = useSearchParams();
  const consultMode = searchParams.get("mode") === "consult";
  const consultTrack = searchParams.get("track");
  const consultEntry = searchParams.get("entry") || "homie";
  const stage2Agenda = consultMode ? getStage2ForTrack(consultTrack) : null;
  const stage2Human = consultMode ? getStage2HumanForTrack(consultTrack) : null;
  const isLifeMart = previewMode === "lifemart" || previewMode === "lifemart-with-card";
  const [messages, setMessages] = useState<Message[]>(
    previewMode === "lifemart-with-card"
      ? [
          { role: "user", content: "My employer offers benefits — is Home Approach one of them?" },
          {
            role: "assistant",
            content:
              "Great question! Home Approach partners with **LifeMart** to give employees free access to all our homebuying tools, plus a 25-point welcome bonus. Let me show you how to unlock it 👇",
            lifemartCard: true,
          },
        ]
      : []
  );
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [conversationLoaded, setConversationLoaded] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [savedStack, setSavedStack] = useState<{
    zip: string;
    total_estimated: number;
    created_at: string;
    isStale: boolean;
  } | null>(null);

  const quickReplies = stage2Agenda
    ? stage2Agenda.quickReplies
    : isLifeMart
    ? QUICK_REPLIES_LIFEMART
    : user
    ? QUICK_REPLIES_USER
    : QUICK_REPLIES_GUEST;

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  // Load persisted conversation for logged-in users (skip in preview modes)
  useEffect(() => {
    if (!user || conversationLoaded || isLifeMart) return;
    // In Stage 2 consult mode, start a fresh focused conversation seeded by Homie.
    if (stage2Agenda) {
      setMessages([{ role: "assistant", content: stage2Agenda.greeting }]);
      setConversationLoaded(true);
      dgFeatureClick("stage2_consult_chat_opened", `${consultTrack}:${consultEntry}`);
      return;
    }
    supabase
      .from("coach_conversations")
      .select("messages")
      .eq("user_id", user.id)
      .maybeSingle()
      .then(({ data }) => {
        if (data?.messages && Array.isArray(data.messages) && data.messages.length > 0) {
          setMessages(data.messages as Message[]);
        }
        setConversationLoaded(true);
      });
  }, [user, conversationLoaded, isLifeMart, stage2Agenda, consultTrack, consultEntry]);

  // Pull the user's most recent saved grant stack so we can show a context
  // chip and proactively nudge a refresh if it's gone stale (>60 days).
  useEffect(() => {
    if (!user || isLifeMart) return;
    let cancelled = false;
    (async () => {
      const { data } = await (supabase as any)
        .from("grant_stack_results")
        .select("zip,total_estimated,created_at")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (cancelled || !data) return;
      const ageDays = (Date.now() - new Date(data.created_at).getTime()) / 86_400_000;
      setSavedStack({
        zip: data.zip,
        total_estimated: data.total_estimated ?? 0,
        created_at: data.created_at,
        isStale: ageDays > 60,
      });
    })();
    return () => { cancelled = true; };
  }, [user, isLifeMart]);

  // Count chat opens for everyone, signed in or not (Data Graph drops guests).
  useEffect(() => {
    if (previewMode !== "default") return;
    logLpEventOnce(fullPage ? "homie_chat_open_full" : "homie_chat_open_bubble", "homie_chat_open", {
      surface: fullPage ? "full_page" : "bubble",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sendText = useCallback(async (text: string) => {
    if (!text || isLoading) return;
    if (accessRef.current.accessLoading) return;
    if (!accessRef.current.hasAccess) {
      if (homieMessagesRemaining(freeUsedRef.current) === 0) return;
      const next = freeUsedRef.current + 1;
      freeUsedRef.current = next;
      setFreeUsed(next);
      try { localStorage.setItem("ha_homie_free_msgs", String(next)); } catch { /* ignore */ }
    }
    if (previewMode === "default") {
      logLpEvent("homie_chat_message", {
        surface: fullPage ? "full_page" : "bubble",
        turn: messages.filter((m) => m.role === "user").length + 1,
      });
    }

    const userMsg: Message = { role: "user", content: text };
    const updatedMessages = [...messages, userMsg];
    setMessages(updatedMessages);
    setIsLoading(true);

    let assistantSoFar = "";

    try {
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };

      // If logged in, use user's token
      if (user) {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.access_token) {
          headers.Authorization = `Bearer ${session.access_token}`;
        }
      }

      const resp = await fetch(HOMIE_URL, {
        method: "POST",
        headers,
        body: JSON.stringify({
          messages: updatedMessages,
          ...(stage2Agenda
            ? { consultMode: true, consultTrack }
            : {}),
        }),
      });

      if (!resp.ok || !resp.body) {
        const err = await resp.json().catch(() => ({ error: "Something went wrong" }));
        const errMsg: Message = { role: "assistant", content: err.error || "Sorry, something went wrong. Please try again." };
        setMessages([...updatedMessages, errMsg]);
        setIsLoading(false);
        return;
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let textBuffer = "";
      let streamDone = false;

      while (!streamDone) {
        const { done, value } = await reader.read();
        if (done) break;
        textBuffer += decoder.decode(value, { stream: true });

        let newlineIndex: number;
        while ((newlineIndex = textBuffer.indexOf("\n")) !== -1) {
          let line = textBuffer.slice(0, newlineIndex);
          textBuffer = textBuffer.slice(newlineIndex + 1);

          if (line.endsWith("\r")) line = line.slice(0, -1);
          if (line.startsWith(":") || line.trim() === "") continue;
          if (!line.startsWith("data: ")) continue;

          const jsonStr = line.slice(6).trim();
          if (jsonStr === "[DONE]") { streamDone = true; break; }

          try {
            const parsed = JSON.parse(jsonStr);
            const content = parsed.choices?.[0]?.delta?.content as string | undefined;
            if (content) {
              assistantSoFar += content;
              const snapshot = assistantSoFar;
              setMessages((prev) => {
                const last = prev[prev.length - 1];
                if (last?.role === "assistant") {
                  return prev.map((m, i) => (i === prev.length - 1 ? { ...m, content: snapshot } : m));
                }
                return [...prev, { role: "assistant", content: snapshot }];
              });
            }
          } catch {
            textBuffer = line + "\n" + textBuffer;
            break;
          }
        }
      }
    } catch {
      const errMsg: Message = { role: "assistant", content: "Sorry, I couldn't connect. Please try again!" };
      setMessages((prev) => [...prev, errMsg]);
    }

    setIsLoading(false);
  }, [messages, isLoading, user, stage2Agenda, consultTrack]);

  // Honor an externally seeded prompt (e.g. "Walk me through my Stack")
  // dispatched via the Homie bridge from Stack / Grant Finder.
  const seedConsumedRef = useRef(false);
  useEffect(() => {
    if (!seedPrompt || seedConsumedRef.current) return;
    seedConsumedRef.current = true;
    sendText(seedPrompt);
    onSeedConsumed?.();
  }, [seedPrompt, sendText, onSeedConsumed]);

  const handleBookHuman = useCallback(async () => {
    if (!consultTrack) return;
    dgFeatureClick(
      "stage2_consult_booked",
      `${consultTrack}:${consultEntry}:${stage2Human?.key ?? "unknown"}`,
    );
    // Mark Stage 2 completed so the dashboard card + success CTA stop nagging.
    try {
      await upsertUserAttributes({
        stage2_consult_completed_at: new Date().toISOString(),
      });
    } catch {
      /* non-fatal — analytics already fired */
    }
    // Fire-and-forget: create the follow-up record + send the expert/member emails.
    if (stage2Human?.key) {
      try {
        // Best-effort: attach the member's most recent saved grant stack so
        // the expert sees what they've already lined up before the call.
        let memberContext: Record<string, unknown> | null = null;
        if (user) {
          try {
            const { data: stack } = await (supabase as any)
              .from("grant_stack_results")
              .select("zip,total_estimated,layers_json,snapshot_json,employer_name,created_at")
              .eq("user_id", user.id)
              .order("created_at", { ascending: false })
              .limit(1)
              .maybeSingle();
            if (stack) memberContext = { grant_stack: stack };
          } catch {
            /* non-fatal */
          }
        }
        await createConsultFollowup({
          data: {
            expertKey: stage2Human.key,
            track: consultTrack,
            entry: consultEntry,
            memberEmail: user?.email ?? null,
            memberContext,
          },
        });
      } catch {
        /* non-fatal — admin can manually create from outcome later */
      }
    }
  }, [consultTrack, consultEntry, stage2Human, user]);

  const startNewConversation = () => {
    setMessages([]);
    setConversationLoaded(true);
    if (user) {
      supabase.from("coach_conversations").upsert(
        { user_id: user.id, messages: [] as any, updated_at: new Date().toISOString() },
        { onConflict: "user_id" }
      );
    }
  };

  const containerClass = fullPage
    ? "flex flex-col h-full w-full"
    : "flex flex-col h-full";

  return (
    <div className={containerClass}>
      {/* Header */}
      <div className={`flex items-center justify-between border-b border-border px-4 py-3 rounded-t-2xl ${isLifeMart ? "bg-gradient-to-r from-primary via-primary to-accent" : "bg-primary"}`}>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-lg">🏠</span>
          <span className="font-bold text-primary-foreground text-sm">Homie</span>
          <span className="text-primary-foreground/70 text-xs">
            {isLifeMart ? "× LifeMart" : "Your Homebuying Guide"}
          </span>
          {isLifeMart && (
            <span className="ml-1 inline-flex items-center gap-1 rounded-full bg-primary-foreground/15 px-2 py-0.5 text-[10px] font-semibold text-primary-foreground">
              <Sparkles className="h-2.5 w-2.5" />
              Member benefit
            </span>
          )}
          {!isLifeMart && savedStack && (
            <Link
              to="/grant-finder"
              title={`Saved Stack for ZIP ${savedStack.zip}`}
              className="ml-1 inline-flex items-center gap-1 rounded-full bg-primary-foreground/15 px-2 py-0.5 text-[10px] font-semibold text-primary-foreground hover:bg-primary-foreground/25 transition-colors"
            >
              <Layers className="h-2.5 w-2.5" />
              Stack ${(savedStack.total_estimated / 1000).toFixed(savedStack.total_estimated >= 10000 ? 0 : 1)}k · {savedStack.zip}
            </Link>
          )}
        </div>
        <div className="flex items-center gap-1">
          {messages.length > 0 && (
            <button onClick={startNewConversation} className="text-primary-foreground/60 hover:text-primary-foreground p-1" title="New conversation">
              <RotateCcw className="h-4 w-4" />
            </button>
          )}
          {!fullPage && (
            <Link to="/homie/chat" className="text-primary-foreground/60 hover:text-primary-foreground p-1" title="Full screen">
              <Expand className="h-4 w-4" />
            </Link>
          )}
        </div>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className={`flex-1 overflow-y-auto p-4 space-y-3 ${fullPage ? "max-w-3xl mx-auto w-full" : ""}`}>
        {messages.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full text-center px-4 gap-4">
            <div className="text-4xl">🏠</div>
            <div>
              {isLifeMart ? (
                <>
                  <h3 className="font-bold text-foreground text-base mb-1">
                    Welcome, LifeMart member! 🎉
                  </h3>
                  <p className="text-sm text-muted-foreground max-w-sm">
                    I'm Homie, your AI homebuying guide — <strong className="text-foreground">free</strong> through your employer benefits.
                    Ask me anything, or claim your <strong className="text-primary">25 bonus points</strong> to get started.
                  </p>
                </>
              ) : (
                <>
                  <h3 className="font-bold text-foreground text-base mb-1">Hey there! I'm Homie</h3>
                  <p className="text-sm text-muted-foreground max-w-sm">
                    Your AI homebuying guide. Whether you're just curious or ready to make an offer, I'm here to help you every step of the way.
                  </p>
                </>
              )}
            </div>
            <div className="flex flex-wrap gap-2 justify-center mt-2">
              {quickReplies.map((q) => (
                <button
                  key={q}
                  onClick={() => { setInput(""); sendText(q); }}
                  className="rounded-full border border-primary/30 bg-primary/5 px-3 py-1.5 text-xs font-medium text-primary hover:bg-primary/10 transition-colors"
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((msg, i) => (
          <div key={i} className="space-y-2">
            <div className={`flex gap-2 ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
              {msg.role === "assistant" && (
                <div className="mt-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm">
                  🏠
                </div>
              )}
              <div
                className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm ${
                  msg.role === "user"
                    ? "bg-primary text-primary-foreground rounded-br-md"
                    : "bg-muted text-foreground rounded-bl-md"
                }`}
              >
                {msg.role === "assistant" ? (
                  <div className="prose prose-sm max-w-none [&>p]:m-0 [&>ul]:my-1 [&>ol]:my-1 [&_a]:text-primary [&_a]:underline">
                    <ReactMarkdown>{msg.content}</ReactMarkdown>
                  </div>
                ) : (
                  msg.content
                )}
              </div>
              {msg.role === "user" && (
                <div className="mt-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary">
                  <User className="h-3.5 w-3.5 text-primary-foreground" />
                </div>
              )}
            </div>
            {msg.lifemartCard && (
              <div className="flex gap-2 justify-start">
                <div className="w-7 shrink-0" />
                <HomieLifeMartCard />
              </div>
            )}
          </div>
        ))}
        {isLoading && messages[messages.length - 1]?.role !== "assistant" && (
          <div className="flex gap-2">
            <div className="mt-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm">
              🏠
            </div>
            <div className="rounded-2xl bg-muted px-3.5 py-2.5 rounded-bl-md">
              <div className="flex gap-1">
                <span className="h-2 w-2 animate-bounce rounded-full bg-muted-foreground/40" style={{ animationDelay: "0ms" }} />
                <span className="h-2 w-2 animate-bounce rounded-full bg-muted-foreground/40" style={{ animationDelay: "150ms" }} />
                <span className="h-2 w-2 animate-bounce rounded-full bg-muted-foreground/40" style={{ animationDelay: "300ms" }} />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Input */}
      <div className={`border-t border-border p-3 ${fullPage ? "max-w-3xl mx-auto w-full" : ""}`}>
        {/* Stack-aware suggested replies — only after Homie has spoken once and we
            have a saved Stack in context. Hidden in LifeMart preview and Stage 2
            consult flows so we don't compete with their own CTAs. */}
        {savedStack && !isLifeMart && !stage2Agenda && messages.some((m) => m.role === "assistant") && (() => {
          const chips = [
            {
              key: "deadline",
              label: "Which grant has the tightest deadline?",
              prompt: `Looking at my saved Stack for ZIP ${savedStack.zip}, which program has the tightest application window or fastest-depleting funds? What should I apply for first?`,
            },
            {
              key: "disqualify",
              label: "What could disqualify me?",
              prompt: `For my saved Stack in ZIP ${savedStack.zip}, what are the most common reasons applicants get disqualified from each layer (income, occupancy, property type, first-time buyer status)?`,
            },
            {
              key: "lender-email",
              label: "Draft my lender intro email",
              prompt: `Draft a short, professional email I can send a lender introducing myself, listing the programs in my saved Stack for ZIP ${savedStack.zip}, and asking if they originate loans that pair with them.`,
            },
          ];
          const stackChipsKey = `homie_stack_chips_dismissed:${savedStack.zip}:${Math.round(savedStack.total_estimated)}`;
          if (typeof window !== "undefined" && sessionStorage.getItem(stackChipsKey) === "1") return null;
          return (
            <div className="mb-2 flex flex-wrap gap-1.5">
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-muted-foreground font-semibold pr-1 self-center">
                <Layers className="h-3 w-3" /> From your Stack
              </span>
              {chips.map((c) => (
                <button
                  key={c.key}
                  type="button"
                  onClick={() => {
                    if (typeof window !== "undefined") sessionStorage.setItem(stackChipsKey, "1");
                    setInput("");
                    sendText(c.prompt);
                  }}
                  className="rounded-full border border-primary/30 bg-primary/5 px-2.5 py-1 text-[11px] font-medium text-primary hover:bg-primary/10 transition-colors"
                >
                  {c.label}
                </button>
              ))}
            </div>
          );
        })()}
        {savedStack?.isStale && !stage2Agenda && (
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-accent/40 bg-accent/5 px-3 py-2">
            <span className="text-xs text-foreground">
              Your Stack for ZIP {savedStack.zip} is{" "}
              {Math.round((Date.now() - new Date(savedStack.created_at).getTime()) / 86_400_000)}{" "}
              days old.{" "}
              <span className="text-muted-foreground">Programs change quarterly.</span>
            </span>
            <Link
              to={`/grant-finder?zip=${savedStack.zip}`}
              className="inline-flex items-center gap-1.5 rounded-md bg-accent text-accent-foreground px-3 py-1.5 text-[11px] font-bold tracking-[0.14em] uppercase hover:opacity-90 transition-opacity"
            >
              <RefreshCw className="h-3 w-3" />
              Refresh Stack
            </Link>
          </div>
        )}
        {stage2Agenda && stage2Human && (
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-accent/40 bg-accent/5 px-3 py-2">
            <span className="text-xs text-foreground">
              Want a real person? <span className="text-muted-foreground">Free 15-min call with {stage2Human.name} · {stage2Human.organization}.</span>
            </span>
            <a
              href={stage2Human.calendlyUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleBookHuman}
              className="inline-flex items-center gap-1.5 rounded-md bg-accent text-accent-foreground px-3 py-1.5 text-[11px] font-bold tracking-[0.14em] uppercase hover:opacity-90 transition-opacity"
            >
              <Calendar className="h-3 w-3" />
              Book 15 min with {stage2Human.name.split(" ")[0]}
            </a>
          </div>
        )}
        {freeLimitReached ? (
          <EmployerAccessGate feature="unlimited Homie chats">{null}</EmployerAccessGate>
        ) : (
        <form
          onSubmit={(e) => { e.preventDefault(); const text = input.trim(); if (text) { setInput(""); sendText(text); } }}
          className="flex items-center gap-2"
        >
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask Homie anything about homebuying..."
            className="flex-1 rounded-xl border border-input bg-background px-4 py-2.5 text-sm placeholder:text-muted-foreground focus:outline-hidden focus:ring-2 focus:ring-ring"
            disabled={isLoading}
          />
          <button
            type="submit"
            disabled={!input.trim() || isLoading}
            className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary text-primary-foreground disabled:opacity-50 hover:opacity-90 transition-opacity"
          >
            <Send className="h-4 w-4" />
          </button>
        </form>
        )}
        {!user && !freeLimitReached && (isLifeMart || messages.length > 2) && (
          <p className="text-xs text-muted-foreground text-center mt-2">
            {isLifeMart ? (
              <>
                Unlock <strong className="text-primary">25 free points</strong> + full member access —{" "}
                <Link to="/lifemart" className="text-primary underline font-semibold">enter your LifeMart code</Link>
              </>
            ) : (
              <>
                <Link
                  to="/auth?source=homie-chat"
                  className="text-primary underline"
                  onClick={() => logLpEvent("homie_signup_click", { prompt: "chat" })}
                >
                  Create a free account
                </Link> to save your conversation and unlock personalized tools.
              </>
            )}
          </p>
        )}
      </div>
    </div>
  );
};

export default HomieChat;
